import type { WidgetDefinition } from '../types/dashboard';
import {
  LargestGainLossWidget,
  PerformanceByTimeOfDayWidget,
  PerformanceByRelativeVolatilityWidget,
  PerformanceByVolumeTradedWidget,
  PerformanceByEntryPercentageOfAtrWidget,
  PerformanceByInTradePriceRangeWidget,
  PerformanceByDurationWidget,
  CumulativePnlWidget,
  CumulativeDrawdownWidget,
  WinningVsLosingWidget,
  AvgWinLossWidget,
  PerformanceByDayOfWeekWidget,
  PerformanceByPriceWidget,
  DailyVolumeWidget,
  TotalFeesWidget,
  ProfitFactorWidget,
  PerformanceByOpeningGapWidget,
  AverageTradePnlWidget,
  PerformanceByVolumeWidget,
  PerformanceByMonthOfYearWidget,
  PerformanceBySymbolAtrWidget,
  MaxConsecutiveWinsWidget,
  MaxConsecutiveLossesWidget,
  PerformanceByMovementWidget,
  RecentTradesWidget,
  LongTermInvestmentsWidget,
  AveragePerShareGainLossWidget,
  TotalGainLossWidget,
  TradePlStandardDeviationWidget,
  AverageDailyGainLossWidget,
  TotalCommissionsWidget,
  NumberOfWinningTradesWidget,
  LargestGainWidget,
  LargestLossWidget,
  NumberOfLosingTradesWidget,
  HoldTimeAnalysisWidget,
} from '../components/widgets';

// Central widget registry - all widgets
export const WIDGET_REGISTRY: Record<string, WidgetDefinition> = {
  // Performance Analysis
  largestGainLoss: {
    id: 'largestGainLoss',
    component: LargestGainLossWidget,
    size: 'mini',
    title: 'Largest Gain vs Largest Loss',
    category: 'performance'
  },
  performanceByTimeOfDay: {
    id: 'performanceByTimeOfDay',
    component: PerformanceByTimeOfDayWidget,
    size: 'regular',
    title: 'Performance By Time Of Day',
    category: 'performance'
  },
  performanceByRelativeVolatility: {
    id: 'performanceByRelativeVolatility',
    component: PerformanceByRelativeVolatilityWidget,
    size: 'regular',
    title: 'Performance By Relative Volatility',
    category: 'performance'
  },
  performanceByVolumeTraded: {
    id: 'performanceByVolumeTraded',
    component: PerformanceByVolumeTradedWidget,
    size: 'regular',
    title: 'Performance By Volume Traded',
    category: 'performance'
  },
  performanceByEntryAtr: {
    id: 'performanceByEntryAtr',
    component: PerformanceByEntryPercentageOfAtrWidget,
    size: 'regular',
    title: 'Performance By Entry % Of ATR',
    category: 'performance'
  },
  performanceByInTradePriceRange: {
    id: 'performanceByInTradePriceRange',
    component: PerformanceByInTradePriceRangeWidget,
    size: 'regular',
    title: 'Performance By In-trade Price Range',
    category: 'performance'
  },
  performanceByDuration: {
    id: 'performanceByDuration',
    component: PerformanceByDurationWidget,
    size: 'regular',
    title: 'Performance By Duration',
    category: 'performance'
  },
  performanceByDay: {
    id: 'performanceByDay',
    component: PerformanceByDayOfWeekWidget,
    size: 'regular',
    title: 'Performance by Day of Week',
    category: 'performance'
  },
  performanceByPrice: {
    id: 'performanceByPrice',
    component: PerformanceByPriceWidget,
    size: 'regular',
    title: 'Performance By Price',
    category: 'performance'
  },
  performanceByOpeningGap: {
    id: 'performanceByOpeningGap',
    component: PerformanceByOpeningGapWidget,
    size: 'regular',
    title: 'Performance By Opening Gap',
    category: 'performance'
  },
  performanceByVolume: {
    id: 'performanceByVolume',
    component: PerformanceByVolumeWidget,
    size: 'regular',
    title: 'Performance By Volume',
    category: 'performance'
  },
  performanceByMonth: {
    id: 'performanceByMonth',
    component: PerformanceByMonthOfYearWidget,
    size: 'regular',
    title: 'Performance By Month Of Year',
    category: 'performance'
  },
  performanceByAtr: {
    id: 'performanceByAtr',
    component: PerformanceBySymbolAtrWidget,
    size: 'regular',
    title: 'Performance by Symbol ATR',
    category: 'performance'
  },
  performanceByMovement: {
    id: 'performanceByMovement',
    component: PerformanceByMovementWidget,
    size: 'regular',
    title: 'Performance By Movement',
    category: 'performance'
  },
  
  // Charts & Visualizations
  cumulativePnl: {
    id: 'cumulativePnl',
    component: CumulativePnlWidget,
    size: 'regular',
    title: 'Cumulative P&L',
    category: 'charts'
  },
  cumulativeDrawdown: {
    id: 'cumulativeDrawdown',
    component: CumulativeDrawdownWidget,
    size: 'regular',
    title: 'Cumulative Drawdown',
    category: 'charts'
  },
  /* winRate widget removed */
  winningVsLosing: {
    id: 'winningVsLosing',
    component: WinningVsLosingWidget,
    size: 'regular',
    title: 'Winning vs Losing Trades',
    category: 'charts'
  },
  dailyVolume: {
    id: 'dailyVolume',
    component: DailyVolumeWidget,
    size: 'regular',
    title: 'Daily Volume',
    category: 'charts'
  },
  averageTradePnl: {
    id: 'averageTradePnl',
    component: AverageTradePnlWidget,
    size: 'regular',
    title: 'Average Trade P&L',
    category: 'charts'
  },
  
  // Statistics
  avgWinLoss: {
    id: 'avgWinLoss',
    component: AvgWinLossWidget,
    size: 'mini',
    title: 'Average Winning Trade vs Losing Trade',
    category: 'statistics'
  },
  totalFees: {
    id: 'totalFees',
    component: TotalFeesWidget,
    size: 'mini',
    title: 'Total Fees',
    category: 'statistics'
  },
  profitFactor: {
    id: 'profitFactor',
    component: ProfitFactorWidget,
    size: 'mini',
    title: 'Profit Factor',
    category: 'statistics'
  },
  maxConsecutiveWins: {
    id: 'maxConsecutiveWins',
    component: MaxConsecutiveWinsWidget,
    size: 'mini',
    title: 'Max Consecutive Wins',
    category: 'statistics'
  },
  maxConsecutiveLosses: {
    id: 'maxConsecutiveLosses',
    component: MaxConsecutiveLossesWidget,
    size: 'mini',
    title: 'Max Consecutive Losses',
    category: 'statistics'
  },
  averagePerShareGainLoss: {
    id: 'averagePerShareGainLoss',
    component: AveragePerShareGainLossWidget,
    size: 'mini',
    title: 'AVERAGE PER SHARE GAIN/LOSS',
    category: 'statistics'
  },
  totalGainLoss: {
    id: 'totalGainLoss',
    component: TotalGainLossWidget,
    size: 'mini',
    title: 'TOTAL GAIN/LOSS',
    category: 'statistics'
  },
  tradePlStandardDeviation: {
    id: 'tradePlStandardDeviation',
    component: TradePlStandardDeviationWidget,
    size: 'mini',
    title: 'Trade PL Standard Deviation',
    category: 'statistics'
  },
  averageDailyGainLoss: {
    id: 'averageDailyGainLoss',
    component: AverageDailyGainLossWidget,
    size: 'mini',
    title: 'AVERAGE DAILY GAIN/LOSS',
    category: 'statistics'
  },
  totalCommissions: {
    id: 'totalCommissions',
    component: TotalCommissionsWidget,
    size: 'mini',
    title: 'Total Commissions',
    category: 'statistics'
  },
  numberOfWinningTrades: {
    id: 'numberOfWinningTrades',
    component: NumberOfWinningTradesWidget,
    size: 'mini',
    title: 'Number of Winning Trades',
    category: 'statistics'
  },
  largestGain: {
    id: 'largestGain',
    component: LargestGainWidget,
    size: 'mini',
    title: 'Largest Gain',
    category: 'statistics'
  },
  
  largestLoss: {
    id: 'largestLoss',
    component: LargestLossWidget,
    size: 'mini',
    title: 'Largest Loss',
    category: 'statistics'
  },
  numberOfLosingTrades: {
    id: 'numberOfLosingTrades',
    component: NumberOfLosingTradesWidget,
    size: 'mini',
    title: 'Number of Losing Trades',
    category: 'statistics'
  },
  holdTimeAnalysis: {
    id: 'holdTimeAnalysis',
    component: HoldTimeAnalysisWidget,
    size: 'mini',
    title: 'Hold Time Analysis',
    category: 'statistics'
  },
  
  
  // Tables & Lists
  openTrades: {
    id: 'openTrades',
    component: RecentTradesWidget,
    size: 'full',
      title: 'Recent Trades',
    category: 'tables'
  },
  longTermInvestments: {
    id: 'longTermInvestments',
    component: LongTermInvestmentsWidget,
    size: 'full',
    title: 'LONG TERM INVESTMENTS',
    category: 'portfolio'
  },
};

// Widget categories for organization
export const WIDGET_CATEGORIES = {
  performance: 'Performance Analysis',
  charts: 'Charts & Visualizations',
  statistics: 'Statistics',
  tables: 'Tables & Lists',
  risk: 'Risk Management',
  portfolio: 'Portfolio Overview',
  market: 'Market Analysis',
  execution: 'Execution Quality'
};

// Default widget configuration
export const getDefaultWidgetConfig = () => {
  return Object.entries(WIDGET_REGISTRY).reduce((acc, [key, widget]) => {
    acc[key] = {
      visible: true, // You can set default visibility rules here
      size: widget.size,
      title: widget.title,
    };
    return acc;
  }, {} as Record<string, any>);
};